import { motion } from 'framer-motion';
import { LESSON } from '@/constants/testIds';

const SortVisualization = ({ array, step, lessonId }) => {
  if (!array || array.length === 0) return null;

  const currentArray = step?.array || array;
  const maxVal = Math.max(...currentArray, 1);
  const minVal = Math.min(...currentArray, 0);
  const range = Math.max(maxVal - minVal, 1);

  const getBarHeight = (value) => {
    const normalized = (value - minVal) / range;
    return Math.max(20, 40 + normalized * 140);
  };

  const getBarStyle = (index) => {
    // Sorted region (from quick sort partition or bubble sort tail)
    if (step?.sorted?.includes(index)) {
      return 'bg-gradient-to-t from-green-600 to-green-400 border-green-500 shadow-[0_0_10px_rgba(34,197,94,0.4)]';
    }
    // Currently comparing
    if (step?.comparing?.includes(index)) {
      return 'bg-gradient-to-t from-[#FFB800] to-[#FFC520] border-[#FFB800] shadow-[0_0_15px_rgba(255,184,0,0.5)]';
    }
    // Pivot (quick sort)
    if (step?.pivotIndex === index) {
      return 'bg-gradient-to-t from-purple-600 to-purple-400 border-purple-500 shadow-[0_0_15px_rgba(168,85,247,0.5)]';
    }
    // Merge sort sub-array indicator
    if (step?.subArray && index >= step.subArray[0] && index <= step.subArray[1]) {
      return 'bg-gradient-to-t from-[#007AFF]/70 to-[#00D4FF]/70 border-[#007AFF]';
    }
    // Swapping
    if (step?.swapping?.includes(index)) {
      return 'bg-gradient-to-t from-red-500 to-orange-400 border-red-500 shadow-[0_0_15px_rgba(239,68,68,0.5)]';
    }
    return 'bg-gradient-to-t from-[#1A1A20] to-[#2A2A30] border-white/20';
  };

  return (
    <div className="space-y-6">
      {/* Bar visualization */}
      <div className="flex items-end justify-center gap-2 min-h-[200px] pb-2">
        {currentArray.map((value, index) => (
          <motion.div
            key={index}
            data-testid={LESSON.arrayCell(index)}
            className="flex flex-col items-center gap-1"
            layout
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <motion.div
              className={`w-10 rounded-t-md border-2 flex items-start justify-center pt-2 ${getBarStyle(index)}`}
              layout
              animate={{
                height: getBarHeight(value)
              }}
              transition={{ type: "spring", stiffness: 200, damping: 20 }}
            >
              <span className="text-white text-xs font-code font-bold">{value}</span>
            </motion.div>
            <div className="text-[10px] text-[#71717A] font-code">{index}</div>
          </motion.div>
        ))}
      </div>

      {/* Info Panels */}
      {step && (
        <div className="grid grid-cols-2 gap-3">
          {step.pass !== undefined && (
            <div className="bg-[#0F0F12] border border-[#FFB800]/20 rounded-lg p-3">
              <div className="text-[#FFB800] text-xs font-code mb-1">
                {lessonId === 'bubble-sort' ? 'PASS' : 'RECURSION LEVEL'}
              </div>
              <div className="text-white text-xl font-code font-bold">{step.pass}</div>
            </div>
          )}
          {step.swapCount !== undefined && (
            <div className="bg-[#0F0F12] border border-red-500/20 rounded-lg p-3">
              <div className="text-red-400 text-xs font-code mb-1">SWAPS</div>
              <div className="text-white text-xl font-code font-bold">{step.swapCount}</div>
            </div>
          )}
          {step.comparisonCount !== undefined && (
            <div className="bg-[#0F0F12] border border-[#007AFF]/20 rounded-lg p-3">
              <div className="text-[#007AFF] text-xs font-code mb-1">COMPARISONS</div>
              <div className="text-white text-xl font-code font-bold">{step.comparisonCount}</div>
            </div>
          )}
          {step.pivot !== undefined && (
            <div className="bg-[#0F0F12] border border-purple-500/20 rounded-lg p-3">
              <div className="text-purple-400 text-xs font-code mb-1">PIVOT</div>
              <div className="text-white text-xl font-code font-bold">{step.pivot}</div>
            </div>
          )}
        </div>
      )}

      {/* Completion indicator */}
      {step?.result && (
        <div className="bg-gradient-to-r from-green-500/10 to-green-400/10 border border-green-500/30 rounded-lg p-4 text-center">
          <div className="text-[#A1A1AA] text-sm font-body mb-1">Sorted Array</div>
          <div className="text-green-400 text-lg font-code font-bold">
            [{step.result.join(', ')}]
          </div>
        </div>
      )}
    </div>
  );
};

export default SortVisualization;
